// ============================================================================
// src/hooks/useAdvisorDirectory.ts
// PV-1052 · M13 — advisor directory + WhatsApp-phone resolver for deal cards
//
// PATTERN — mirrors `useDealDldComparison` (discriminated status + cancellation
// flag, no SWR / React Query). Fetches the advisor list ONCE at mount and
// exposes `resolveWhatsAppPhone(advisorId)`, the assigned→default cascade that
// `DealPage.tsx` already runs inline. Lets the catalogue resolve a card's
// advisor number with a single fetch instead of one per card.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';

import { listAdvisors, type Advisor } from '@/lib/db/admin/advisors';
import { pickAdvisorWhatsAppPhone } from '@/lib/whatsapp';

type Status = 'loading' | 'success' | 'error';

type AdvisorDirectoryState = {
  status: Status;
  advisors: Advisor[];
  error: Error | null;
};

export type AdvisorDirectory = AdvisorDirectoryState & {
  /**
   * Resolve the WhatsApp phone for a deal, walking the cascade assigned advisor
   * → default advisor. Returns `undefined` when neither qualifies (inactive or
   * no dialable phone) OR while the directory is still loading — callers MUST
   * treat `undefined` as "hide the CTA", never as "fall back to the agency
   * number" (no agency fallback on deal cards).
   */
  resolveWhatsAppPhone: (advisorId: string | null | undefined) => string | undefined;
};

const INITIAL_STATE: AdvisorDirectoryState = { status: 'loading', advisors: [], error: null };

// Session-scoped guard so the "default advisor has no dialable phone" dev
// warning fires once, not once per page / render.
let devWarnedNoDefaultPhone = false;

/**
 * Loads the advisor directory once and returns a stable `resolveWhatsAppPhone`
 * resolver. While loading (or on error) the resolver returns `undefined`, so a
 * card's WhatsApp CTA stays hidden until a real advisor number is known.
 */
export function useAdvisorDirectory(): AdvisorDirectory {
  const [state, setState] = useState<AdvisorDirectoryState>(INITIAL_STATE);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL_STATE);

    listAdvisors()
      .then((advisors) => {
        if (cancelled) return;
        setState({ status: 'success', advisors, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          advisors: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const byId = useMemo(() => new Map(state.advisors.map((a) => [a.id, a])), [state.advisors]);
  const defaultAdvisor = useMemo(() => state.advisors.find((a) => a.is_default), [state.advisors]);

  // A non-empty advisors table always has a default (DB invariant, PR #222),
  // but that default may carry no dialable phone — in which case every card
  // CTA for an unassigned (or phoneless) deal is hidden. Surface that data
  // misconfiguration to developers instead of silently dropping the button.
  useEffect(() => {
    if (!import.meta.env.DEV || devWarnedNoDefaultPhone) return;
    if (state.status !== 'success' || state.advisors.length === 0) return;
    if (pickAdvisorWhatsAppPhone(defaultAdvisor) === undefined) {
      devWarnedNoDefaultPhone = true;
      console.warn(
        '[useAdvisorDirectory] No default advisor with a dialable phone — deal-card ' +
          'WhatsApp CTAs are hidden for deals without a phoned assigned advisor.',
      );
    }
  }, [state.status, state.advisors.length, defaultAdvisor]);

  const resolveWhatsAppPhone = useCallback(
    (advisorId: string | null | undefined): string | undefined =>
      pickAdvisorWhatsAppPhone(advisorId ? byId.get(advisorId) : undefined, defaultAdvisor),
    [byId, defaultAdvisor],
  );

  return {
    status: state.status,
    advisors: state.advisors,
    error: state.error,
    resolveWhatsAppPhone,
  };
}
